import { getPlanPermission } from "@utils/permissions/plan-permissions";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { RequirePermission } from "@components/common/require-permission";
import { InfoRow } from "@components/info-row";
import { AdmPlan, SubjectType } from "@custom-types/base-types";
import { Grid } from "@mui-wrapper";
import { DateTimeUtils, UiUtils } from "@stellar/utils";
import {
  getPlanByIdSelector,
  allPlansMapSelector,
} from "@store/plans/plans-selector";
import { updatePlan } from "@store/plans/plans-thunks";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { formatPlanName } from "@utils/data-display";
import { DEFAULT_USER_ID } from "@utils/plan-utils";
import { TableType } from "@utils/track-event/event-types";
import { PlanEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import {
  isUnlimitedPlanEndDate,
  PLAN_DURATION_LABELS,
} from "@utils/plan-duration";
import { useEffect, useState } from "react";
import { ActionTextField, CircularProgress } from "@flat-ui";
import { PlanTokenRows } from "@components/plans-table/plan-token-rows";
import { planAppSettingsOf } from "@utils/product/plan-apps";
import { PlanAppInfoRows } from "@components/plan-app-info-rows";

interface Props {
  /** The ID of the plan to display additional information for */
  planId: AdmPlan["id"];

  /** The type of the table, can be used for different purposes such as tracking events */
  tableType: TableType;

  /** The type of the subject the table belongs to, which decides who may edit the plan */
  subjectType: SubjectType;
}

/** The end date of the plan as shown to the user */
function getEndDateInfo(endDate: AdmPlan["endDate"]): string {
  if (!endDate) {
    return UiUtils.DASH;
  }

  return isUnlimitedPlanEndDate(endDate)
    ? PLAN_DURATION_LABELS.unlimited.option
    : DateTimeUtils.formatDateTimeInUtc({
        date: endDate,
        shouldShowFullDateTime: true,
        shouldShowUtcText: true,
      });
}

/** Component showing additional information of a plan */
export function AdditionalPlanTableRow({
  planId,
  tableType,
  subjectType,
}: Props): React.JSX.Element {
  const { trackEvent } = useTrackEvent();
  const plan = useAppSelector(getPlanByIdSelector(planId));
  const allPlansMap = useAppSelector(allPlansMapSelector());
  const dispatch = useAppDispatch();
  const { handleErrorWithToast, handleErrorWithDialog } = useErrorHandler();
  const [memberEmail, setMemberEmail] = useState<string>("");
  const [isFetchingMember, setIsFetchingMember] = useState<boolean>(false);

  async function onCommentChanged(updatedComment: string): Promise<void> {
    trackEvent({
      name: PlanEvents.updatePlanComment,
      props: {
        location: tableType,
      },
    });
    try {
      await dispatch(
        updatePlan({
          planId,
          updatedProps: {
            type: "updating",
            updatedComment,
          },
        })
      ).unwrap();
    } catch (error) {
      handleErrorWithDialog({
        id: `updating-plan-${Date.now().toString()}`,
        title: "Could not update plan",
        error,
      });
    }
  }

  // Fetches the member which created the plan
  useEffect(() => {
    async function fetchMemberData(): Promise<void> {
      if (!plan) {
        return;
      }
      if (plan.createdBy === DEFAULT_USER_ID) {
        setMemberEmail("System");
        return;
      }

      setIsFetchingMember(true);
      try {
        const { email } = await faroCoreApiClient.admin.getMemberById({
          memberId: plan.createdBy,
        });
        setMemberEmail(email);
      } catch (_error) {
        setMemberEmail("Unknown");
      } finally {
        setIsFetchingMember(false);
      }
    }

    void fetchMemberData();
  }, [dispatch, handleErrorWithToast, plan]);

  if (!plan) {
    return <>Plan not found</>;
  }

  /** Retrieves the parent (attached) plan if it exists */
  const parentSubscriptionId = plan.metadata?.parentSubscriptionId;

  /** If the parent subscription ID exists, retrieves the corresponding parent plan */
  const parentPlan = parentSubscriptionId
    ? allPlansMap[parentSubscriptionId]
    : undefined;

  return (
    <Grid container sx={{ width: "100%", py: "47px" }}>
      <Grid size={{ xs: 6 }}>
        <InfoRow
          label="Name"
          info={formatPlanName(plan)}
          tooltip={plan.label}
        />

        <InfoRow label="Id" info={plan.id} />

        <PlanAppInfoRows appSettings={planAppSettingsOf(plan)} />

        <InfoRow
          label="Created By"
          info={isFetchingMember ? <CircularProgress size={15} /> : memberEmail}
        />

        {parentSubscriptionId && (
          <InfoRow
            label="Attached to plan"
            info={
              parentPlan ? formatPlanName(parentPlan) : parentSubscriptionId
            }
            // Only show the id in the tooltip if the parent plan is found,
            // otherwise it would show the same content as the info
            tooltip={parentPlan ? parentSubscriptionId : undefined}
          />
        )}

        <InfoRow
          label="Comments"
          info={
            <RequirePermission
              permission={getPlanPermission(subjectType, "edit")}
            >
              <ActionTextField
                inputText={plan.description ?? ""}
                onCancelButtonClick={() => {}}
                onConfirmButtonClick={onCommentChanged}
                sx={{ p: 0 }}
              />
            </RequirePermission>
          }
        />

        {/* One row per year of tokens the plan includes, as the token plans that grant them
            are not listed in the table on their own */}
        <PlanTokenRows planId={planId} />
      </Grid>

      <Grid size={{ xs: 6 }}>
        <InfoRow
          label="Created Date"
          info={DateTimeUtils.formatDateTimeInUtc({
            date: plan.createdAt,
            shouldShowFullDateTime: true,
            shouldShowUtcText: true,
          })}
        />
        <InfoRow
          label="Start Date"
          info={DateTimeUtils.formatDateTimeInUtc({
            date: plan.startDate,
            shouldShowFullDateTime: true,
            shouldShowUtcText: true,
          })}
        />
        <InfoRow label="End Date" info={getEndDateInfo(plan.endDate)} />

        {plan.status.identifier === "invalid" && plan.status.message && (
          <InfoRow label="Deactivation reason" info={plan.status.message} />
        )}
      </Grid>
    </Grid>
  );
}
